import { canBlend, disguiseCity, exposeCooldownLeft, isHidden, rivalLandCity } from '../spies';
import { territoryOwners } from '../territory';
import type { EntityId, PlayerId, SimContext, SimState, SystemFn } from '../types';
import { maintainGroups } from './formation';

/**
 * The `blend` command (see spies.ts `canBlend`): the spy stops where it is,
 * off automation and out of its group (formation would move it), and is
 * disguised in the city whose territory it stands in.
 */
function handleBlend(state: SimState, ctx: SimContext, playerId: PlayerId, unitId: EntityId): void {
  const check = canBlend(state, ctx.rules, playerId, unitId, undefined, ctx.terrain);
  if (!check.ok) return;
  const unit = state.units[unitId];
  delete unit.path;
  delete unit.order;
  delete unit.auto;
  delete unit.blocked;
  unit.progress = 0;
  if (unit.groupId !== undefined) {
    delete unit.groupId;
    maintainGroups(state);
  }
  delete unit.lurk;
  unit.disguise = { cityId: check.cityId };
}

/**
 * The `expose` command: `playerId` points at unit `unitId`. A spy disguised in
 * one of their cities is exposed (seen and fought like any unit from now on,
 * its disguise over). Anything else — their own worker, a unit that isn't
 * there, a spy that isn't disguised with them — is a false accusation: they
 * can't expose again for `pacing.exposeCooldown` seconds. Refused (no effect)
 * while that lasts. The outcome only depends on disguises in their own
 * cities, which they're meant to find out about this way.
 */
function handleExpose(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitId: EntityId,
  dt: number
): void {
  const player = state.players[playerId];
  if (!player || player.eliminated || exposeCooldownLeft(state, playerId) > 0) return;
  delete player.exposeCooldownUntil;
  const unit = state.units[unitId];
  const city = unit?.disguise ? state.buildings[unit.disguise.cityId] : undefined;
  if (unit && !unit.exposed && unit.owner !== playerId && city?.owner === playerId) {
    unit.exposed = true;
    delete unit.disguise;
    return;
  }
  const ticks = Math.round(ctx.rules.pacing.exposeCooldown / dt);
  if (ticks > 0) player.exposeCooldownUntil = state.tick + ticks;
}

/**
 * Ends every disguise that no longer holds (spies.ts `disguiseCity`): the spy
 * moved or left the territory, its city fell or it was exposed. Then an
 * undisguised hidden spy in a rival completed city's territory counts
 * `Unit.lurk` up and is exposed once it reaches `pacing.spyGrace` seconds
 * (the count restarts whenever it's out of rival land or blends in). Runs
 * after combat, so the vision recorded this tick (exploration) is already
 * right.
 */
const spies: SystemFn = (state, ctx, _inputs, dt) => {
  const { rules } = ctx;
  let owners: ReturnType<typeof territoryOwners> | undefined;
  const grace = Math.max(1, Math.round(rules.pacing.spyGrace / dt));
  for (const unit of Object.values(state.units)) {
    if (unit.disguise) {
      owners ??= territoryOwners(state, rules);
      if (!disguiseCity(state, rules, unit, owners)) delete unit.disguise;
    }
    if (unit.disguise || !isHidden(rules, unit)) {
      delete unit.lurk;
      continue;
    }
    owners ??= territoryOwners(state, rules);
    if (!rivalLandCity(state, rules, unit, owners)) {
      delete unit.lurk;
      continue;
    }
    unit.lurk = (unit.lurk ?? 0) + 1;
    if (unit.lurk >= grace) {
      unit.exposed = true;
      delete unit.lurk;
    }
  }
  for (const player of Object.values(state.players)) {
    if (player.exposeCooldownUntil !== undefined && player.exposeCooldownUntil <= state.tick) {
      delete player.exposeCooldownUntil;
    }
  }
};

export { handleBlend, handleExpose, spies };
